// When the SPA may quietly ask again, in its own module with no imports so
// Node's test runner can check it under --experimental-strip-types.
// useBackend.ts pulls in the Supabase client, which only resolves in a browser
// build, so the decision that matters cannot be tested from there. Same reason
// routeKind.ts sits beside it.
//
// WHY THIS EXISTS
//
// 30 August 2026. The cockpit was showing "write CONNECTION_CLOSED
// ...hyperdrive.local:5432" and the two access banners after most actions, and
// the only recovery was reloading the page. A reload works because it simply
// asks again, which is the whole content of this file: the second attempt
// nearly always succeeds, and there was no reason for a person to be the one
// making it.
//
// The API now retries once on its own (api/src/index.ts) and answers 503 with
// `transient: true` when that did not clear it. This is the second net, for the
// case where the fault is between the browser and Cloudflare rather than
// between Cloudflare and Postgres, which the Worker cannot see at all.

/** The error shape api/src/index.ts sends for a transient fault. */
export type ServerErrorBody = {
  error?: string
  transient?: boolean
  /**
   * 'safe' means the API states that no handler statement reached the database,
   * so resending cannot repeat an effect. 'unknown' means the connection died
   * mid-write and whether it landed is not known, which is exactly when a client
   * must NOT decide for the operator.
   */
  retry?: 'safe' | 'unknown'
}

/**
 * Gateway-class statuses. 503 is ours. 502 and 504 come from Cloudflare when the
 * Function itself did not answer, which is the same kind of event seen from one
 * layer further out.
 */
const TRANSIENT_STATUSES = new Set([502, 503, 504])

/**
 * True when the response may be asked for again without telling the operator.
 *
 * A read is always safe to re-ask. A write is resent ONLY when the server said
 * in so many words that nothing ran. Guessing in the other direction is how an
 * approval gets applied twice, and no amount of convenience is worth that.
 */
export function shouldRetryResponse(
  status: number,
  body: ServerErrorBody,
  isRead: boolean,
): boolean {
  if (!TRANSIENT_STATUSES.has(status)) return false
  if (isRead) return true
  return body.retry === 'safe'
}

/**
 * True when `fetch` itself rejected, so there is no response at all.
 *
 * Reads only. A rejected fetch does not say whether the request reached the
 * server: the wifi may have dropped before it left, or after the write
 * committed and before the answer came back. For a read that difference does not
 * matter; for a write it is the whole question.
 */
export function shouldRetryNetworkFailure(isRead: boolean): boolean {
  return isRead
}

export const NETWORK_UNREACHABLE =
  'Could not reach the Cockpit. Check your connection and try again.'

/** Long enough for a dropped connection to be replaced, short enough to feel like nothing happened. */
export const RETRY_DELAY_MS = 250
